package io.horion.auth.presentation.viewmodel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import io.horion.auth.api.models.domain.LoginMethod
import io.horion.auth.api.models.domain.UserProfile
import io.horion.auth.domain.models.Role
import io.horion.auth.domain.models.SsoCodeResponse
import io.horion.auth.domain.usecase.AuthUseCase
import io.horion.auth.domain.usecase.ProfileUseCase
import io.horion.auth.presentation.state.AccountUiModel
import io.horion.auth.presentation.state.AuthUiState
import io.horion.auth.utils.LocalStorageManager
import io.horion.auth.utils.UserEnvironmentHelper
import io.horion.auth.utils.Validators
import kotlinx.coroutines.*
import kotlin.coroutines.cancellation.CancellationException
import kotlin.js.Date
import kotlin.time.Duration.Companion.milliseconds

class AuthViewModel(
    private val authUserCase: AuthUseCase,
    private val profileUseCase: ProfileUseCase
) {
    var uiState by mutableStateOf(AuthUiState())
        private set

    private val viewModelScope = CoroutineScope(Dispatchers.Main)
    private var cooldownJob: Job? = null

    fun loadProfileFromLocalStorage(): Boolean {
        val savedProfile = LocalStorageManager.getUserProfile() ?: return false

        val profileExists = savedProfile.firstName.isNotBlank() || savedProfile.lastName.isNotBlank()

        uiState = uiState.copy(
            isProfileCreated = profileExists,
            userEmail = savedProfile.email.orEmpty(),
            userPhone = savedProfile.phone.orEmpty(),
            userFirstName = savedProfile.firstName,
            userLastName = savedProfile.lastName,
            userRole = savedProfile.role,
            city = savedProfile.city,
            country = savedProfile.country,
            timezone = savedProfile.timezone,
            authMethod = if (LoginMethod.EMAIL.name == savedProfile.primaryAuthMethod) LoginMethod.EMAIL else LoginMethod.PHONE,
            is2FAEnabled = savedProfile.isTwoFactorEnabled,
            loginTimestamp = formatTimestamp(savedProfile.loginTimestamp)
        )
        return true
    }

    suspend fun fetchAndSyncProfile() {
        try {
            val remoteUser = profileUseCase.readUser() ?: run {
                loadProfileFromLocalStorage()
                return
            }

            val currentActiveUuid = remoteUser.uuid
            val localProfile = LocalStorageManager.getUserProfileByUuid(currentActiveUuid)

            val clientTimezone = UserEnvironmentHelper.getLocalTimezone()
            val clientCountryCode = UserEnvironmentHelper.getCountryCodeFromBrowser()
            val clientCountryName = UserEnvironmentHelper.getCountryNameFromCode(clientCountryCode)
            val clientInferredCity = UserEnvironmentHelper.inferCityFromTimezone(clientTimezone)

            val updatedProfile = UserProfile(
                id = currentActiveUuid,
                firstName = remoteUser.firstName,
                lastName = remoteUser.lastName,
                avatar = remoteUser.avatar,
                role = remoteUser.role.name,

                // Garantit qu'on conserve les identifiants spécifiques à ce compte
                email = localProfile?.email ,
                phone = localProfile?.phone ,
                primaryAuthMethod = localProfile?.primaryAuthMethod ?: LoginMethod.EMAIL.name,

                city = clientInferredCity.ifBlank { remoteUser.city },
                country = clientCountryName.ifBlank { remoteUser.country },
                countryCode = clientCountryCode.ifBlank { remoteUser.countryCode },
                timezone = clientTimezone.ifBlank { remoteUser.timezone },

                isTwoFactorEnabled = remoteUser.isTwoFactorEnabled,
                loginTimestamp = Date.now().toLong(),
                isLoggedIn = true
            )

            // Sauvegarde ciblée dans le dictionnaire
            LocalStorageManager.saveUserProfile(updatedProfile)

            profileUseCase.updateUser(
                firstName = updatedProfile.firstName,
                lastName = updatedProfile.lastName,
                avatar = updatedProfile.avatar
            )

        } catch (e: Exception) {
            loadProfileFromLocalStorage()
        }
    }

    fun createProfile(firstName: String, lastName: String, avatar: String = "", is2FAEnabled: Boolean = false) {
        viewModelScope.launch {
            uiState = uiState.copy(isSavingProfile = true, profileSaveError = null)

            val response = profileUseCase.saveUser(
                firstName = firstName,
                lastName = lastName,
                avatar = avatar,
            )

            if (response.success) {
                loadProfileFromLocalStorage()
                uiState = uiState.copy(
                    isSavingProfile = false,
                    profileSaveSuccess = true,
                    isEditingProfile = false,
                    isProfileCreated = true
                )
            } else {
                uiState = uiState.copy(
                    isSavingProfile = false,
                    profileSaveError = response.message
                )
            }
        }
    }

    fun updatePersonalInfo(newFirstName: String, newLastName: String) {
        viewModelScope.launch {
            uiState = uiState.copy(isSavingProfile = true, profileSaveError = null)

            val response = profileUseCase.updateUser(
                firstName = newFirstName,
                lastName = newLastName,
                avatar = uiState.avatarUrl,
            )

            if (response.success) {
                val currentLocalProfile = LocalStorageManager.getUserProfile()

                // Utilisation du pattern .copy() pour conserver l'ID, le rôle et le reste du cache
                val updatedProfile = currentLocalProfile?.copy(
                    firstName = newFirstName,
                    lastName = newLastName,
                    email = uiState.userEmail,
                    phone = uiState.userPhone,
                    primaryAuthMethod = uiState.authMethod.name,
                    city = uiState.city,
                    country = uiState.country,
                    timezone = uiState.timezone
                ) ?: UserProfile(
                    firstName = newFirstName,
                    lastName = newLastName,
                    email = uiState.userEmail,
                    phone = uiState.userPhone,
                    primaryAuthMethod = uiState.authMethod.name,
                    isLoggedIn = true
                )

                LocalStorageManager.saveUserProfile(updatedProfile)
                loadProfileFromLocalStorage()

                uiState = uiState.copy(
                    isSavingProfile = false,
                    profileSaveSuccess = true,
                    isEditingProfile = false
                )
            } else {
                uiState = uiState.copy(
                    isSavingProfile = false,
                    profileSaveError = response.message
                )
            }
        }
    }

    private fun formatTimestamp(timestamp: Long): String {
        if (timestamp <= 0L) return ""
        val date = Date(timestamp)
        return "${date.toLocaleDateString()} ${date.toLocaleTimeString()}"
    }

    fun onMethodChange(method: LoginMethod) {
        uiState = uiState.copy(method = method)
        clearError()
    }

    fun onCountryCodeChange(value: String) {
        uiState = uiState.copy(countryCode = value)
        clearError()
    }

    fun onIsMobileClient(checked: Boolean) {
        uiState = uiState.copy(isMobileClient = checked)
        clearError()
    }

    fun onNewsletterChange(checked: Boolean) {
        uiState = uiState.copy(newsletterChecked = checked)
        clearError()
    }

    fun onIdentifierChange(identifier: String) {
        uiState = uiState.copy(identifier = identifier, identifierError = null)
        clearError()
    }

    fun onEmailChange(email: String) {
        uiState = uiState.copy(email = email, emailError = null)
        clearError()
    }

    fun onPhoneChange(phone: String) {
        uiState = uiState.copy(phone = phone, phoneError = null)
        clearError()
    }

    fun onPasswordChange(password: String) {
        uiState = uiState.copy(password = password, passwordError = null)
        clearError()
    }

    fun onConfirmPasswordChange(confirmPassword: String) {
        uiState = uiState.copy(confirmPassword = confirmPassword, confirmPasswordError = null)
        clearError()
    }

    fun onChangeCode(input: String) {
        uiState = uiState.copy(code = input)
    }

    fun onChangeRedirect(input: String) {
        uiState = uiState.copy(redirect = input)
        clearError()
    }

    fun clearErrors() {
        uiState = uiState.copy(
            emailError = null,
            phoneError = null,
            codeError = null,
            passwordError = null,
            generalError = null
        )
    }

    fun startEditProfile() {
        uiState = uiState.copy(
            isEditingProfile = true,
            profileSaveSuccess = false,
            profileSaveError = null,
        )
    }

    fun cancelEditProfile() {
        uiState = uiState.copy(
            isEditingProfile = false,
            profileSaveError = null,
        )
    }

    fun onFirstNameChange(value: String) {
        uiState = uiState.copy(firstName = value)
        clearError()
    }

    fun onLastNameChange(value: String) {
        uiState = uiState.copy(lastName = value)
        clearError()
    }

    fun onAddressChange(value: String) {
        uiState = uiState.copy(address = value)
        clearError()
    }

    fun onProfilePhoneChange(value: String) {
        uiState = uiState.copy(phone = value)
        clearError()
    }

    fun onProfileEmailChange(value: String) {
        uiState = uiState.copy(email = value)
        clearError()
    }

    suspend fun checkSession() : Boolean {
        uiState = uiState.copy(isLoading = true)
        return try {
            // Supposons que authUserCase.checkSession() renvoie un objet
            // contenant : { connected: Boolean, accounts: List<AccountDomainModel> }
            val response = authUserCase.checkSession(uiState.service)
            console.log(response)
            if (response.connected) {
                val mappedAccounts = response.accounts.map { acc ->
                    AccountUiModel(
                        userUuid = acc.userUuid,
                        email = acc.email ?: "",
                        phone = acc.phone ?: "",
                        role = acc.role ?: Role.USER.name,
                        canAccessService = acc.canAccessService,
                        isActive = acc.isActive
                    )
                }

                uiState = uiState.copy(
                    isLoading = false,
                    accounts = mappedAccounts
                )
                true
            } else {
                uiState = uiState.copy(isLoading = false, accounts = emptyList())
                false
            }
        } catch (e: CancellationException) {
            throw e
        } catch (t: Throwable) {
            uiState = uiState.copy(isLoading = false, accounts = emptyList())
            false
        }
    }

    fun switchAccount(userUuid: String) {
        uiState = uiState.copy(
            isLoading = true,
            authErrorMessage = null,
            loginSucceed = false
        )

        viewModelScope.launch {
            try {
                val result = authUserCase.switchAccount(userUuid)
                if (result.success) {
                    // 1. Définir le compte comme actif dans le LocalStorage
                    LocalStorageManager.setActiveUserUuid(userUuid)

                    // 2. Synchroniser le profil distant vers le LocalStorage
                    fetchAndSyncProfile()

                    // 3. Mettre à jour l'UI avec la liste actualisée des comptes
                    val updatedAccountsList = uiState.accounts.map { account ->
                        account.copy(isActive = (account.userUuid == userUuid))
                    }

                    uiState = uiState.copy(
                        isLoading = false,
                        loginSucceed = true,
                        accounts = updatedAccountsList
                    )
                } else {
                    uiState = uiState.copy(
                        isLoading = false,
                        authErrorMessage = result.message ?: "Échec du changement de compte"
                    )
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                uiState = uiState.copy(
                    isLoading = false,
                    authErrorMessage = "Erreur réseau lors du changement de compte"
                )
            }
        }
    }

    fun logoutAccount(userUuid: String) {
        viewModelScope.launch {
            LocalStorageManager.removeAccount(userUuid)
            try {
//                val result = authUserCase.logoutLocal(userUuid)
//                if (result.success) {
//                    // Retire le compte localement dans l'UI State
//                    val updatedAccounts = uiState.accounts.filterNot { it.userUuid == userUuid }
//                    uiState = uiState.copy(accounts = updatedAccounts)
//
//                    // Si plus aucun compte ne reste, réinitialise pour afficher le formulaire
//                    if (updatedAccounts.isEmpty()) {
//                        clear()
//                    }
//                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                console.log("❌ Échec de la déconnexion du compte $userUuid")
            }
        }
    }

    suspend fun generateSsoCodeForMobile(): SsoCodeResponse? {
        return try {
            authUserCase.generateSsoCodeForMobile()
        } catch (t: Throwable) {
            null
        }
    }

    fun onCurrentPasswordChange(value: String) {
        uiState = uiState.copy(currentPassword = value, passwordChangeError = null)
        clearError()
    }

    fun onNewPasswordChange(value: String) {
        uiState = uiState.copy(newPassword = value, passwordChangeError = null)
        clearError()
    }

    fun onConfirmNewPasswordChange(value: String) {
        uiState = uiState.copy(confirmNewPassword = value, passwordChangeError = null)
        clearError()
    }

    fun toggleTwoFactor(enable: Boolean) {
        viewModelScope.launch {
            uiState = try {
                uiState.copy(twoFactorEnabled = enable)
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                uiState.copy(twoFactorEnabled = !enable)
            }
        }
        clearError()
    }

    fun getProfile() {
        viewModelScope.launch {
            try {
                // TODO: Implémentation future du profil
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                // Silencieux
            }
        }
    }

    fun isFormValid(passwordConfirm: String = ""): Boolean {
        return (Validators.isValidEmail(uiState.email) || Validators.isValidPhone(uiState.phone))
                && (uiState.password.isNotBlank())
                && (uiState.password == passwordConfirm)
                && (Validators.isValidPassword(uiState.password))
    }

    fun cleanAuthentificationSuccess() {
        uiState = uiState.copy(authenticationSucceed = false)
    }

    fun resetLogoutState() {
        uiState = uiState.copy(logoutSucceed = false)
    }

    fun clear() {
        uiState = AuthUiState(redirect = uiState.redirect)
    }

    fun clearError() {
        uiState = uiState.copy(authErrorMessage = null)
    }

    private fun startCooldown() {
        cooldownJob?.cancel()
        cooldownJob = viewModelScope.launch {
            var remaining = 60
            while (remaining > 0) {
                delay(1000.milliseconds)
                remaining--
                uiState = uiState.copy(otpCooldownSeconds = remaining)
            }
        }
    }

    /// ---- 2FA

    fun initiate2FASetup() {
        viewModelScope.launch {
            // 1. On affiche la modale et le loader
            uiState = uiState.copy(
                show2FASetupModal = true,
                isToggling2FA = true,
                twoFactorSetupError = null,
                twoFactorBackupCodes = null // On nettoie au cas où
            )

            try {
                // TODO: Appel API Ktor -> POST /api/auth/2fa/init
                // val response = repository.init2FA()

                // Simulation d'un délai réseau
                delay(800.milliseconds)

                // 2. On met à jour l'UI avec le QR Code et la clé reçus du serveur
                uiState = uiState.copy(
                    isToggling2FA = false,
                    // Remplace par les vraies données de ton API
                    qrCodeUrl = "https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=otpauth://totp/Orion:goube@test.com?secret=JBSWY3DPEHPK3PXP&issuer=Orion",
                    secretKey = "JBSWY3DPEHPK3PXP"
                )
            } catch (e: Exception) {
                uiState = uiState.copy(
                    isToggling2FA = false,
                    twoFactorSetupError = "Erreur lors de l'initialisation de la 2FA."
                )
            }
        }
    }

    fun verifyAndEnable2FA(verificationCode: String) {
        if (verificationCode.length < 6) return

        viewModelScope.launch {
            uiState = uiState.copy(
                isToggling2FA = true,
                twoFactorSetupError = null
            )

            try {
                // TODO: Appel API Ktor -> POST /api/auth/2fa/verify (envoi du code)
                // val response = repository.verify2FA(verificationCode)

                // Simulation d'un délai réseau
                delay(1000.milliseconds)

                // Simulation d'une erreur si le code est invalide (pour tester)
                if (verificationCode == "000000") {
                    uiState = uiState.copy(
                        isToggling2FA = false,
                        twoFactorSetupError = "Code invalide. Veuillez réessayer."
                    )
                    return@launch
                }

                // 3. Succès ! Le backend valide le code et nous renvoie les Recovery Codes
                val mockBackupCodes = listOf(
                    "A1B2-C3D4", "E5F6-G7H8", "I9J0-K1L2", "M3N4-O5P6",
                    "Q7R8-S9T0", "U1V2-W3X4", "Y5Z6-A7B8", "C9D0-E1F2"
                )

                uiState = uiState.copy(
                    isToggling2FA = false,
                    twoFactorEnabled = true, // L'UI du profil basculera au vert
                    twoFactorBackupCodes = mockBackupCodes // Affichera l'étape 2 de la modale
                )
            } catch (e: Exception) {
                uiState = uiState.copy(
                    isToggling2FA = false,
                    twoFactorSetupError = "Erreur lors de la vérification du code."
                )
            }
        }
    }

    fun complete2FASetup() {
        // Appelée quand l'utilisateur clique sur "J'ai bien enregistré mes codes"
        uiState = uiState.copy(
            show2FASetupModal = false,
            qrCodeUrl = null,
            secretKey = null,
            twoFactorBackupCodes = null,
            twoFactorSetupError = null
        )
    }

    // Afficher le Modal
    fun initiateSecondaryChannelSetup() {
        uiState = uiState.copy(
            show2FASetupModal = true,
            twoFactorSetupError = null
        )
    }

    // Annuler / Fermer le Modal
    fun cancel2FASetup() {
        uiState = uiState.copy(
            show2FASetupModal = false,
            twoFactorSetupError = null
        )
    }

    // ÉTAPE 1 : Envoyer le code au canal secondaire choisi
    fun sendOTPToSecondaryChannel(inputValue: String, onSuccess: () -> Unit) {
        if (inputValue.isBlank()) {
            uiState = uiState.copy(twoFactorSetupError = "Veuillez entrer une coordonnée valide.")
            return
        }

        val method = if (uiState.authMethod == LoginMethod.EMAIL) "PHONE" else "EMAIL"

        uiState = uiState.copy(isToggling2FA = true, twoFactorSetupError = null)

        viewModelScope.launch {
            var isSuccess = false
            var errorMsg: String? = null

            try {
                val response = authUserCase.send2FaOtp(
                    channelValue = inputValue,
                    method = method
                )
                isSuccess = response.success
                if (!response.success) errorMsg = response.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                errorMsg = "Impossible d'envoyer le code OTP"
            } finally {
                if (isSuccess) {
                    uiState = uiState.copy(isToggling2FA = false, twoFactorSetupError = null)
                    onSuccess() // Callback UI pour passer à l'étape 2 (saisie de l'OTP)
                } else {
                    uiState = uiState.copy(
                        isToggling2FA = false,
                        twoFactorSetupError = errorMsg ?: "Échec de l'envoi"
                    )
                }
            }
        }
    }

    // ÉTAPE 2 : Valider le code OTP et activer la 2FA
    fun verifyOTPAndEnable2FA(inputValue: String, code: String) {
        if (code.length < 6) {
            uiState = uiState.copy(twoFactorSetupError = "Code invalide")
            return
        }

        val method = if (uiState.authMethod == LoginMethod.EMAIL) "PHONE" else "EMAIL"

        uiState = uiState.copy(isToggling2FA = true, twoFactorSetupError = null)

        viewModelScope.launch {
            var isSuccess = false
            var errorMsg: String? = null

            try {
                val response = authUserCase.verify2FaAndEnable(
                    channelValue = inputValue,
                    method = method,
                    code = code
                )
                isSuccess = response.success
                if (!response.success) errorMsg = response.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                errorMsg = "Erreur lors de la validation"
            } finally {
                if (isSuccess) {
                    val updatedPhone = if (method == "PHONE") inputValue else uiState.userPhone
                    val updatedEmail = if (method == "EMAIL") inputValue else uiState.userEmail

                    // 1. Sauvegarde dans le LocalStorage
                    LocalStorageManager.update2FaProfile(
                        isTwoFactorEnabled = true,
                        newEmail = updatedEmail,
                        newPhone = updatedPhone
                    )

                    // 2. Mise à jour de l'état UI
                    uiState = uiState.copy(
                        isToggling2FA = false,
                        is2FAEnabled = true,
                        userPhone = updatedPhone,
                        userEmail = updatedEmail,
                        show2FASetupModal = false,
                        twoFactorSetupError = null
                    )
                } else {
                    uiState = uiState.copy(
                        isToggling2FA = false,
                        twoFactorSetupError = errorMsg ?: "Code incorrect"
                    )
                }
            }
        }
    }

    // Désactiver la 2FA
    fun disable2FA() {
        uiState = uiState.copy(isToggling2FA = true, twoFactorSetupError = null)

        viewModelScope.launch {
            var isSuccess = false
            try {
                val response = authUserCase.disable2FA()
                isSuccess = response.success
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                isSuccess = false
            } finally {
                if (isSuccess) {
                    LocalStorageManager.update2FaProfile(isTwoFactorEnabled = false)
                    uiState = uiState.copy(
                        isToggling2FA = false,
                        is2FAEnabled = false
                    )
                } else {
                    uiState = uiState.copy(
                        isToggling2FA = false,
                        twoFactorSetupError = "Impossible de désactiver la 2FA"
                    )
                }
            }
        }
    }

    fun logoutLocal(userUuid: String, service: String) {
        viewModelScope.launch {
            try {
                val result = authUserCase.logoutLocal(service, userUuid)

                if (result == null || !result.success) {
                    LocalStorageManager.removeAccount(userUuid)
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) { }
        }
    }

    // ─── ACTIONS ROBUSTES ET SÉCURISÉES ───────────────────────────────────────

    fun login() {
        uiState = uiState.copy(isLoading = true, loginSucceed = false, authErrorMessage = null)

        viewModelScope.launch {
            try {
                val result = authUserCase.login(
                    email = uiState.email,
                    phone = uiState.phone,
                    password = uiState.password,
                    isMobileClient = uiState.redirect.contains("://callback")
                )

                if (result.success) {
                    // 1. On débloque l'UI Immédiatement pour rediriger l'utilisateur
                    uiState = uiState.copy(
                        isLoading = false,
                        codeLogin = result.message,
                        loginSucceed = true,
                        authErrorMessage = null
                    )

                    // 2. Synchro du profil en arrière-plan sans bloquer
                    launch {
                        fetchAndSyncProfile()
                    }
                } else {
                    uiState = uiState.copy(
                        isLoading = false,
                        authErrorMessage = result.message,
                        loginSucceed = false
                    )
                }
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                uiState = uiState.copy(
                    isLoading = false,
                    authErrorMessage = "Impossible de joindre le serveur. Vérifiez votre connexion.",
                    loginSucceed = false
                )
            }
        }
    }

    fun register() {
        if (uiState.isLoading) return
        if (uiState.registerSucceed) { verify(); return }

        uiState = uiState.copy(isLoading = true, registerSucceed = false, authErrorMessage = null)
        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                val result = authUserCase.register(
                    email = uiState.email,
                    phone = uiState.phone,
                    password = uiState.password
                )
                nextSuccess = result.success
                if (!result.success) nextMessage = result.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Une erreur est survenue lors de l'inscription. Veuillez réessayer."
            } finally {
                uiState = if (nextSuccess) {
                    uiState.copy(isLoading = false, registerSucceed = true)
                } else {
                    uiState.copy(isLoading = false, authErrorMessage = nextMessage ?: "Échec de l'inscription")
                }
            }
        }
    }

    fun verify(phone: String = "") {
        uiState = uiState.copy(isLoading = true, verifySucceed = false, authErrorMessage = null)
        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                val response = authUserCase.verify(uiState.email, uiState.phone, uiState.method)
                nextSuccess = response.success
                if (!response.success) nextMessage = response.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Impossible d'envoyer le code de validation. Merci de réessayer."
            } finally {
                if (nextSuccess) {
                    startCooldown()
                    uiState = uiState.copy(isAuthenticating = false, verifySucceed = true, isLoading = false)
                } else {
                    uiState = uiState.copy(isAuthenticating = false, isLoading = false, authErrorMessage = nextMessage ?: "Échec de l'envoi")
                }
            }
        }
    }

    fun confirm() {
        uiState = uiState.copy(isLoading = true, confirmSucceed = false, authErrorMessage = null)
        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                val response = authUserCase.confirm(uiState.email, uiState.phone, uiState.code, uiState.method)
                nextSuccess = response.success
                if (!response.success) nextMessage = response.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Validation impossible. Veuillez réessayer."
            } finally {
                uiState = if (nextSuccess) {
                    clear()
                    uiState.copy(isLoading = false, confirmSucceed = true, authErrorMessage = null)
                } else {
                    uiState.copy(isLoading = false, authErrorMessage = nextMessage ?: "Code incorrect")
                }
            }
        }
    }

    fun forgotPassword() {
        uiState = uiState.copy(isLoading = true, verifySucceed = false, authErrorMessage = null)
        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                val response = authUserCase.forgotPassword(uiState.email, uiState.phone)
                nextSuccess = response.success
                if (!response.success) nextMessage = response.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Une erreur est survenue. Veuillez vérifier vos accès réseau."
            } finally {
                if (nextSuccess) {
                    startCooldown()
                    uiState = uiState.copy(isLoading = false, verifySucceed = true, authErrorMessage = null)
                } else {
                    uiState = uiState.copy(isLoading = false, authErrorMessage = nextMessage ?: "Une erreur est survenue")
                }
            }
        }
    }

    fun resetPassword() {
        uiState = uiState.copy(isLoading = true, resetPasswordSucceed = false, authErrorMessage = null)
        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                val response = authUserCase.resetPassword(uiState.email, uiState.phone, uiState.code, uiState.password)
                nextSuccess = response.success
                if (!response.success) nextMessage = response.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Réinitialisation échouée. Veuillez réessayer."
            } finally {
                uiState = if (nextSuccess) {
                    uiState.copy(isLoading = false, resetPasswordSucceed = true)
                } else {
                    uiState.copy(isLoading = false, authErrorMessage = nextMessage ?: "Échec de l'opération")
                }
            }
        }
    }

    fun logout() {
        uiState = uiState.copy(isLoading = true, logoutSucceed = false, authErrorMessage = null)
        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                val result = authUserCase.logout()
                nextSuccess = (result != null && result.success)
                if (!nextSuccess) nextMessage = result?.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Déconnexion impossible."
            } finally {
                uiState = if (nextSuccess) {
                    uiState.copy(logoutSucceed = true, isLoading = false)
                } else {
                    uiState.copy(isLoading = false, authErrorMessage = nextMessage)
                }
            }
        }
    }

    fun changePassword() {
        val state = uiState
        when {
            state.currentPassword.isBlank() -> {
                uiState = uiState.copy(passwordChangeError = "Le mot de passe actuel est requis")
                return
            }
            state.newPassword.length < 6 -> {
                uiState = uiState.copy(passwordChangeError = "Le nouveau mot de passe doit contenir au moins 6 caractères")
                return
            }
            state.newPassword != state.confirmNewPassword -> {
                uiState = uiState.copy(passwordChangeError = "Les mots de passe ne correspondent pas")
                return
            }
        }

        uiState = uiState.copy(isChangingPassword = true, passwordChangeError = null)
        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                val result = authUserCase.resetPassword(
                    email = uiState.email,
                    phone = uiState.phone,
                    code = uiState.currentPassword,
                    newPassword = uiState.newPassword
                )
                nextSuccess = result.success
                if (!result.success) nextMessage = result.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Une erreur est survenue. Veuillez réessayer."
            } finally {
                if (nextSuccess) {
                    uiState = uiState.copy(
                        isChangingPassword = false,
                        passwordChangeSuccess = true,
                        currentPassword = "",
                        newPassword = "",
                        confirmNewPassword = "",
                    )
                    delay(3000)
                    uiState = uiState.copy(passwordChangeSuccess = false)
                } else {
                    uiState = uiState.copy(isChangingPassword = false, passwordChangeError = nextMessage ?: "Une erreur est survenue")
                }
            }
        }
    }


    fun requestPasswordChangeOtp() {
        val identifierEmail = uiState.userEmail
        val identifierPhone = uiState.userPhone

        uiState = uiState.copy(
            isSendingPasswordOtp = true,
            passwordOtpSent = false,
            passwordChangeError = null
        )

        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                // Utilise l'email ou le téléphone de l'utilisateur connecté
                val response = authUserCase.forgotPassword(
                    email = identifierEmail.orEmpty(),
                    phone = identifierPhone.orEmpty()
                )
                nextSuccess = response.success
                if (!response.success) nextMessage = response.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Impossible d'envoyer le code de vérification."
            } finally {
                if (nextSuccess) {
                    startCooldown() // Si tu as un timer pour l'envoi de code
                    uiState = uiState.copy(
                        isSendingPasswordOtp = false,
                        passwordOtpSent = true,
                        passwordChangeError = null
                    )
                } else {
                    uiState = uiState.copy(
                        isSendingPasswordOtp = false,
                        passwordOtpSent = false,
                        passwordChangeError = nextMessage ?: "Échec de l'envoi du code"
                    )
                }
            }
        }
    }

    fun confirmPasswordReset() {
        // Validations locales rapides
        if (uiState.code.isBlank()) {
            uiState = uiState.copy(passwordChangeError = "Veuillez saisir le code reçu")
            return
        }
        if (uiState.newPassword.isBlank()) {
            uiState = uiState.copy(passwordChangeError = "Le nouveau mot de passe ne peut pas être vide")
            return
        }
        if (uiState.newPassword != uiState.confirmNewPassword) {
            uiState = uiState.copy(passwordChangeError = "Les mots de passe ne correspondent pas")
            return
        }

        uiState = uiState.copy(
            isChangingPassword = true,
            passwordChangeSuccess = false,
            passwordChangeError = null
        )

        viewModelScope.launch {
            var nextSuccess = false
            var nextMessage: String? = null
            try {
                val response = authUserCase.resetPassword(
                    email = uiState.userEmail.orEmpty(),
                    phone = uiState.userPhone.orEmpty(),
                    code = uiState.code,
                    newPassword = uiState.newPassword
                )
                nextSuccess = response.success
                if (!response.success) nextMessage = response.message
            } catch (e: CancellationException) {
                throw e
            } catch (e: Exception) {
                nextMessage = "Échec de la modification. Veuillez réessayer."
            } finally {
                if (nextSuccess) {
                    uiState = uiState.copy(
                        isChangingPassword = false,
                        passwordChangeSuccess = true,
                        passwordOtpSent = false,
                        code = "",
                        newPassword = "",
                        confirmNewPassword = "",
                        passwordChangeError = null
                    )
                } else {
                    uiState = uiState.copy(
                        isChangingPassword = false,
                        passwordChangeError = nextMessage ?: "Code invalide ou expiré"
                    )
                }
            }
        }
    }

    fun resetPasswordForm() {
        uiState = uiState.copy(
            passwordOtpSent = false,
            code = "",
            newPassword = "",
            confirmNewPassword = "",
            passwordChangeError = null
        )
    }
}